// 모듈 레지스트리 — 모듈 목록·경로 접두어·번들 코드의 단일 소스 (부수효과 없는 순수 함수만 둔다).
//
// 이전에는 ModuleHost 의 PATH_TO_MODULE, Layout 의 MODULE_CODES, Sidebar·TopNav 의 경로 분기가
// 같은 목록을 따로 들고 있었다. 이제 이 파일의 레지스트리 한 벌에서 모두 파생한다.
//
// 데이터 출처 (앞이 이긴다):
//   1. basicinfo 의 GET /api/basicinfo/common/modules  (s_module 표 — 새 모듈은 시드 한 행 + FE 번들 배포로 끝)
//   2. 아래 FALLBACK_ENTRIES (API 실패·빈 배열·옛 서버·첫 로딩 전)
//
// 폴백 상수를 지우는 시점: 모든 환경(로컬·개발·운영)의 basicinfo 가 /modules 를 서빙하고,
// 호스트 직접 모듈(board·onechamber·oneffice·videoconf·basicinfo)도 표로 옮겨진 뒤.
// 그 전까지 폴백은 "API 가 없어도 지금과 똑같이 동작"을 보장하는 안전망이다.
//
// 주의: 이 파일은 node --experimental-strip-types 로 시험된다 — enum·namespace·파라미터 프로퍼티 금지,
// 다른 파일 import 금지.

export type LangCode = 'ko' | 'ja' | 'en';

/** 'erp' — 사이드바 ERP 그룹에 동적으로 채워지는 모듈 / 'host' — 호스트가 직접 연결하는 모듈 */
export type ModuleGroup = 'erp' | 'host';

export interface ModuleEntry {
  /** 모듈 코드 — API 의 moduleCd (fi·tax·sd …). 호스트 직접 모듈은 board·basicinfo 같은 자체 키 */
  moduleCd: string;
  /** 번들 코드 — ModuleHost 의 MODULES 키 (yukimushi-sd-fe …) */
  bundle: string;
  /** 경로 접두어 — '/finance'. 끝 슬래시 없음 */
  pathPrefix: string;
  /** 표시 이름(언어별). 없으면 호출부가 번역 키(nav.*)로 폴백한다 */
  names: { ko?: string; ja?: string; en?: string };
  sortNo: number;
  /** false 면 사이드바·탭에서 숨긴다(직접 URL 진입은 막지 않는다) */
  visible: boolean;
  group: ModuleGroup;
}

/** GET /api/basicinfo/common/modules 응답 한 행 (필요한 필드만) */
export interface ApiModule {
  moduleCd: string;
  nameKr?: string | null;
  nameJp?: string | null;
  nameEn?: string | null;
  feBundleCd: string;
  pathPrefix: string;
  lcnYn?: boolean;
  sortNo?: number | null;
}

const NO_NAMES = {};

function fb(moduleCd: string, bundle: string, pathPrefix: string, sortNo: number, group: ModuleGroup): ModuleEntry {
  return { moduleCd, bundle, pathPrefix, names: NO_NAMES, sortNo, visible: true, group };
}

/**
 * 폴백 — 구 ModuleHost.PATH_TO_MODULE 전체. 사이드바 ERP 그룹의 기존 순서를 sortNo 로 옮겼다
 * (finance·tax·basicinfo·pjt·ops·hr·sd·mm·pp·log·co·crm). basicinfo 는 호스트 직접 모듈이지만
 * 사이드바에서는 ERP 그룹 한가운데에 있어 group 'erp' 로 둔다(이름·아이콘은 호출부의 정적 정의를 쓴다).
 */
export const FALLBACK_ENTRIES: ModuleEntry[] = [
  fb('base-webpack', 'yukimushi-base-webpack-fe', '/yukimushi-base-webpack-fe', 0, 'host'),
  fb('base-vite', 'yukimushi-base-vite-fe', '/yukimushi-base-vite-fe', 0, 'host'),
  fb('board', 'yukimushi-board-fe', '/board', 0, 'host'),
  fb('onechamber', 'yukimushi-onechamber-fe', '/onechamber', 0, 'host'),
  fb('oneffice', 'yukimushi-oneffice-fe', '/oneffice', 0, 'host'),
  fb('videoconf', 'yukimushi-videoconf-fe', '/videoconf', 0, 'host'),
  fb('basicinfo', 'yukimushi-basicinfo-fe', '/basicinfo', 25, 'erp'),
  fb('fi', 'yukimushi-fi-fe', '/finance', 10, 'erp'),
  fb('tax', 'yukimushi-tax-fe', '/tax', 20, 'erp'),
  fb('pjt', 'yukimushi-pjt-fe', '/pjt', 30, 'erp'),
  fb('ops', 'yukimushi-ops-fe', '/ops', 40, 'erp'),
  fb('hr', 'yukimushi-hr-fe', '/hr', 50, 'erp'),
  fb('sd', 'yukimushi-sd-fe', '/sd', 60, 'erp'),
  fb('mm', 'yukimushi-mm-fe', '/mm', 70, 'erp'),
  fb('pp', 'yukimushi-pp-fe', '/pp', 80, 'erp'),
  fb('log', 'yukimushi-log-fe', '/log', 90, 'erp'),
  fb('co', 'yukimushi-co-fe', '/co', 100, 'erp'),
  fb('crm', 'yukimushi-crm-fe', '/crm', 110, 'erp'),
  // fb('mail', 'yukimushi-mail-fe', '/mail', ...)  — 3단계에서 등록 예정
];

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** 경로 접두어 정규화 — '/' 로 시작, 끝 슬래시 제거. 잘못된 값이면 '' */
export function normalizePrefix(v: unknown): string {
  const s = str(v);
  if (!s || s[0] !== '/') return '';
  const t = s.replace(/\/+$/, '');
  return t; // '/' 만 온 경우도 ''(홈은 모듈이 아니다)
}

/** API 응답을 안전하게 정규화 — 배열이 아니거나 필수 필드(moduleCd·feBundleCd·pathPrefix)가 없는 행은 버린다 */
export function normalizeApiModules(raw: unknown): ApiModule[] {
  if (!Array.isArray(raw)) return [];
  const out: ApiModule[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const moduleCd = str(r.moduleCd).toLowerCase();
    const feBundleCd = str(r.feBundleCd);
    const pathPrefix = normalizePrefix(r.pathPrefix);
    if (!moduleCd || !feBundleCd || !pathPrefix) continue;
    out.push({
      moduleCd,
      feBundleCd,
      pathPrefix,
      nameKr: str(r.nameKr) || null,
      nameJp: str(r.nameJp) || null,
      nameEn: str(r.nameEn) || null,
      lcnYn: r.lcnYn === false ? false : true, // 필드 누락은 사용 가능으로 본다
      sortNo: typeof r.sortNo === 'number' && isFinite(r.sortNo) ? r.sortNo : null,
    });
  }
  return out;
}

/**
 * 폴백 위에 API 를 병합한다.
 *  - 같은 moduleCd: API 가 경로·번들·이름·순서·노출을 덮어쓴다(그룹은 폴백 유지)
 *  - API 에 없는 폴백 모듈(호스트 직접 모듈 포함): 경로·번들은 그대로 두어 직접 URL 진입은 풀리지만,
 *    **API 가 확정 응답(authoritative)이면 사이드바·탭에서 숨긴다**(visible=false) — 서버가 사용자 권한으로 모듈을 거르므로
 *    목록에 없는 모듈은 이 사용자에게 보일 메뉴가 없다는 뜻이다(2026-10-14). 확정 응답이 아니면(API 실패·옛 서버·형식 오류) 폴백이 그대로 보인다
 *  - API 에만 있는 모듈은 'erp' 그룹으로 추가한다
 * 결과는 sortNo(같으면 폴백 순서) 순으로 정렬한다.
 */
export function mergeRegistry(fallback: ModuleEntry[], api: ApiModule[], authoritative: boolean = false): ModuleEntry[] {
  const byCd = new Map<string, ModuleEntry>();
  for (const e of fallback) {
    byCd.set(e.moduleCd, authoritative ? { ...e, visible: false } : e); // 확정 응답이면 API 가 말하지 않은 모듈은 숨김부터 시작
  }
  for (const a of api) {
    const base = byCd.get(a.moduleCd);
    byCd.set(a.moduleCd, {
      moduleCd: a.moduleCd,
      bundle: a.feBundleCd,
      pathPrefix: a.pathPrefix,
      names: { ko: a.nameKr || undefined, ja: a.nameJp || undefined, en: a.nameEn || undefined },
      sortNo: a.sortNo != null ? a.sortNo : (base ? base.sortNo : 9999),
      visible: a.lcnYn !== false,
      group: base ? base.group : 'erp',
    });
  }
  const order = new Map<string, number>();
  fallback.forEach((e, i) => order.set(e.moduleCd, i));
  return Array.from(byCd.values()).sort((x, y) => {
    if (x.sortNo !== y.sortNo) return x.sortNo - y.sortNo;
    return (order.has(x.moduleCd) ? order.get(x.moduleCd)! : 9999) - (order.has(y.moduleCd) ? order.get(y.moduleCd)! : 9999);
  });
}

/** 원시 API 응답(또는 캐시)로 레지스트리를 만든다 — 어떤 입력이든 던지지 않고, 쓸 게 없으면 폴백 그대로 */
export function buildRegistry(raw: unknown): ModuleEntry[] {
  const api = normalizeApiModules(raw);
  return mergeRegistry(FALLBACK_ENTRIES, api, isAuthoritative(raw, api));
}

/**
 * 확정 응답인가 — 배열이고, 비었거나(= 이 사용자에게 보일 모듈이 없다) 쓸 수 있는 행이 하나라도 있다.
 * 배열이 아니거나 모든 행이 깨졌으면(옛 서버·SPA fallback·형식 오류) 확정이 아니라 폴백을 그대로 쓴다.
 */
export function isAuthoritative(raw: unknown, api: ApiModule[]): boolean {
  return Array.isArray(raw) && (raw.length === 0 || api.length > 0);
}

export const FALLBACK_REGISTRY: ModuleEntry[] = buildRegistry(null);

/** 경로가 접두어 아래인지 — 경로 경계까지 맞춘다('/co' 가 '/community'·'/login' 을 삼키지 않게) */
export function isPathUnder(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

/** 경로에 해당하는 모듈 항목 — 접두어가 겹치면 더 긴 쪽이 이긴다. 숨김(lcnYn=false) 모듈도 찾는다(직접 진입 허용) */
export function entryForPath(registry: ModuleEntry[], pathname: string): ModuleEntry | null {
  let hit: ModuleEntry | null = null;
  for (const e of registry) {
    if (isPathUnder(pathname, e.pathPrefix) && (!hit || e.pathPrefix.length > hit.pathPrefix.length)) hit = e;
  }
  return hit;
}

/** 경로 → 번들 코드 (구 ModuleHost.moduleCodeForPath) */
export function moduleCodeForPath(registry: ModuleEntry[], pathname: string): string | null {
  const e = entryForPath(registry, pathname);
  return e ? e.bundle : null;
}

/** keep-alive 패널 대상 번들 코드 — 숨김 모듈도 포함(직접 URL 진입이 동작해야 한다). 중복 제거 */
export function bundleCodes(registry: ModuleEntry[]): string[] {
  const seen: Record<string, true> = {};
  const out: string[] = [];
  for (const e of registry) {
    if (!seen[e.bundle]) { seen[e.bundle] = true; out.push(e.bundle); }
  }
  return out;
}

/** 사이드바·탭에 보일 항목 — 그룹별, 숨김 제외, 이미 정렬된 순서 유지 */
export function navEntries(registry: ModuleEntry[], group: ModuleGroup): ModuleEntry[] {
  return registry.filter(e => e.group === group && e.visible);
}

/** 표시 이름 — 해당 언어 → 한국어 → 폴백(번역 키 결과) 순. 공백 문자열은 비어 있는 것으로 본다 */
export function entryLabel(entry: ModuleEntry, lang: LangCode, fallbackLabel: string): string {
  return str(entry.names[lang]) || str(entry.names.ko) || fallbackLabel;
}

/** 레지스트리가 사실상 같은지(경로·번들·이름·순서·노출) — 같으면 상태를 갈아끼워 재렌더하지 않는다 */
export function sameRegistry(a: ModuleEntry[], b: ModuleEntry[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
